import { useMutation } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Input,
  Label
} from '@chain-zen/ui'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { ENABLE_TRANSPOREON_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { TCustomer } from '../../../types/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'
import { TransporeonUpdateModal } from './transporeon-update-modal'
import { getTransporeonGroupStructure, getTransporeonPayloadStructure } from './utils'

type TFieldValues = {
  [key: string]: string
}

interface ICustomField {
  disabled?: boolean
  integrationConfigurationID?: string
  isRequired?: boolean
  labelText: string
  name: string
  placeholder?: string
}

type TTransporeonEnableAndUpdateAppInput = {
  appType: AppTypeEnum
  customers: TCustomer[]
  password: string
  username: string
}

type TCustomerGroups = Record<string, ICustomField[]>

/**
 * Modal for enabling the Transporeon app
 * Allows entering API key and customer IDs with validation
 */
export function TransporeonModal({ appType, isOpen, isModalEditable, setModalEditable, app, onClose }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [customers, setCustomers] = useState<TCustomerGroups>(() => getTransporeonGroupStructure(1))
  const [deletedGroups, setDeletedGroups] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<TFieldValues>({
    defaultValues: {
      password: '',
      username: ''
    }
  })

  // Methods for managing customer fields
  const removeFieldsFromSchema = (id: string) => {
    setDeletedGroups((prev) => [...prev, id])
  }

  const handleAddMore = () => {
    const uniqueID = uuidv4()
    const newlyAddedGroup = getTransporeonGroupStructure(uniqueID)
    setCustomers((old) => {
      return {
        ...old,
        ...newlyAddedGroup
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    reset()
    if (setModalEditable) {
      setModalEditable(false)
    }
    onClose()
  }

  const handleGroupDelete = (groupID: string) => {
    if (Object.keys(customers).length > 1) {
      removeFieldsFromSchema(groupID)
      setCustomers((old) => {
        const newGroups = { ...old }
        delete newGroups[groupID]
        return newGroups
      })
    }
  }

  // Mutation for enabling Transporeon app
  const [enableTransporeonApp, { loading }] = useMutation<
    { enableTransporeonApp: { appEnabled: boolean } },
    TTransporeonEnableAndUpdateAppInput
  >(ENABLE_TRANSPOREON_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Transporeon app is enabled successfully!')
      onClose()
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  // Handler for form submission
  const handleSaveDetails = (data: TFieldValues) => {
    // Create a copy of the data to avoid modifying the original
    const formData = { ...data }

    // Remove deleted fields from the copy
    deletedGroups.forEach((id) => {
      delete formData[`customerNameInChaine-${id}`]
    })

    const payload = getTransporeonPayloadStructure(formData)

    enableTransporeonApp({
      variables: {
        appType: appType,
        customers: payload.customers,
        password: formData.password,
        username: formData.username
      }
    })
  }

  // If app is in edit mode and we have app data, render the UpdateModal instead
  if (isModalEditable && app) {
    return <TransporeonUpdateModal appType={appType} isOpen={isOpen} onClose={onClose} app={app} />
  }

  const getCustomerFields = () => {
    return Object.entries(customers).map(([groupId, fields]) => (
      <div key={groupId} className="bg-muted p-4 rounded-lg mb-4" role="group" aria-labelledby={`group-${groupId}`}>
        <div className="flex justify-end mb-2">
          {Object.keys(customers).length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleGroupDelete(groupId)}
              aria-label={`Remove customer group ${groupId}`}
            >
              Remove
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {fields.map((field: ICustomField) => {
            const errorId = errors[field.name] ? `${field.name}-error` : undefined
            return (
              <div key={field.name}>
                <Label htmlFor={field.name} className="block text-sm font-medium mb-1">
                  {field.labelText}{' '}
                  {!!field.isRequired && (
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  )}
                </Label>
                <Input
                  id={field.name}
                  type="text"
                  placeholder={field.placeholder}
                  disabled={field.disabled || false}
                  aria-required={field.isRequired ? 'true' : 'false'}
                  aria-describedby={errorId}
                  aria-invalid={!!errors[field.name]}
                  {...register(field.name, {
                    required: field.isRequired ? `${field.labelText} is required` : false
                  })}
                />
                {!!errors[field.name] && (
                  <p className="text-sm text-red-500 mt-1" id={errorId} role="alert">
                    {errors[field.name]?.message}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    ))
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Enable Transporeon for Chain</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Connect your Transporeon account to enable tracking integration.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleSaveDetails)} className="space-y-4" noValidate={true}>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="username" id="username-label">
                Username
              </Label>
              <Input
                id="username"
                placeholder="Transporeon username"
                aria-required="true"
                aria-describedby={errors.username ? 'username-error' : undefined}
                aria-invalid={!!errors.username}
                {...register('username', { required: 'Username is required' })}
              />
              {errors.username?.message ? (
                <p className="text-sm text-red-500" id="username-error" role="alert">
                  {errors.username.message}
                </p>
              ) : null}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" id="password-label">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Transporeon password"
                aria-required="true"
                aria-describedby={errors.password ? 'password-error' : undefined}
                aria-invalid={!!errors.password}
                {...register('password', { required: 'Password is required' })}
              />
              {errors.password?.message ? (
                <p className="text-sm text-red-500" id="password-error" role="alert">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="block text-sm font-medium">
                  Customers{' '}
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMore}
                  aria-label="Add another customer"
                >
                  Add More
                </Button>
              </div>

              {getCustomerFields()}
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex flex-col gap-2">
              {!!formError && (
                <p className="text-sm text-red-500" role="alert">
                  {formError}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                <a
                  href="https://handbook.chaineapp.com/product-guide/apps/transporeon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  aria-label="Read the Transporeon integration guide (opens in a new tab)"
                >
                  See detailed steps here <ExternalLink className="h-4 w-4" />
                </a>
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || isSubmitting}>
                {loading || isSubmitting ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
